/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import React, { memo, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Alert, CircularProgress, Stack, Typography } from '@mui/material';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';
import { ReportableApiError } from '../../../../components/ReportableApiError';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { BasicRoutes } from '../../../../constants/basicRoutes';
import { Namespaces } from '../../../../i18n';
import { OrganizationApiService } from '../../../../services/OrganizationApiService';
import { fetchOidcConfiguration, fetchTeams } from '../../../../utils/queryFns';
import { validationSchema } from './SsoTab.consts';
import { SsoTabForm } from './SsoTab.Form';
import { OidcConfigurationValues } from './SsoTab.types';

type Props = {
  organization: {
    id: string;
    name: string;
  };
};

export const SsoTab = memo<Props>(props => {
  const { t } = useTranslation(Namespaces.OrganizationAdmin);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const oidcConfigQuery = useQuery([fetchOidcConfiguration.key, props.organization.id], fetchOidcConfiguration.fn, {
    refetchOnWindowFocus: false,
    retry: (_count, err: any) => err.status !== 404,
  });
  const oidcConfig = useMemo(() => oidcConfigQuery.data?.payload, [oidcConfigQuery.data]);

  const teamsQuery = useQuery([fetchTeams.key, { organizationId: props.organization.id }], fetchTeams.fn);
  const teams = useMemo(() => teamsQuery.data?.payload, [teamsQuery.data]);

  const onSubmit = async (values: OidcConfigurationValues) => {
    setIsFetching(true);
    try {
      await OrganizationApiService.setOidcConfiguration(props.organization.id, {
        enabled: values.enabled,
        issuerUrl: values.issuerUrl.trim(),
        clientId: values.clientId.trim(),
        clientSecret: values.clientSecret.trim(),
        verifyCertificate: values.verifyCertificate,
        domains: values.domains,
        groupAttribute: values.groupAttribute?.trim(),
      });
      await oidcConfigQuery.refetch();
    } catch (error) {
      await notifyReportableApiError(error);
    } finally {
      setIsFetching(false);
    }
  };

  const onConfirmUpdateStatus = async (values: OidcConfigurationValues) => {
    setIsFetching(true);
    await onSubmit({
      ...values,
      enabled: !values.enabled,
    });
    setIsOpenModal(false);
  };

  const isOidcConfigError = useMemo(() => {
    const error = oidcConfigQuery.error as any;
    if (error && error.status === 500) return true;
    return false;
  }, [oidcConfigQuery.error]);

  return isOidcConfigError ? (
    <Alert severity="error">
      <ReportableApiError error={oidcConfigQuery.error as any} />
    </Alert>
  ) : oidcConfigQuery.isLoading ? (
    <AlignCenter>
      <CircularProgress color="primary" />
    </AlignCenter>
  ) : (
    <Formik<OidcConfigurationValues>
      enableReinitialize={true}
      initialValues={
        oidcConfig
          ? oidcConfig
          : {
              enabled: true,
              issuerUrl: '',
              clientId: '',
              clientSecret: '',
              verifyCertificate: true,
              domains: [],
              groupAttribute: '',
            }
      }
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {formProps => (
        <div style={{ display: 'flex', gap: '50px' }}>
          <Stack sx={{ width: '580px' }}>
            <SsoTabForm
              {...formProps}
              oidcConfig={oidcConfig}
              setIsOpenModal={setIsOpenModal}
              isFetching={isFetching}
            />
            <SimpleConfirmModal
              open={isOpenModal}
              message={t('oidc.status.actions.confirm', {
                action: formProps.values.enabled
                  ? t('oidc.status.actions.deactivate').toLowerCase()
                  : t('oidc.status.actions.activate').toLowerCase(),
              })}
              onCancel={() => setIsOpenModal(false)}
              onConfirm={() => onConfirmUpdateStatus(formProps.values)}
            />
          </Stack>
          <div style={{ width: 'fit-content', height: 'fit-content' }}>
            <Typography sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px' }}>
              {t('oidc.hint.main')}
              <ul style={{ paddingLeft: '25px', margin: 0 }}>
                <li>
                  <Trans
                    i18nKey={`${Namespaces.OrganizationAdmin}:oidc.hint.root_url`}
                    values={{ url: window.origin }}
                    components={{ b: <b /> }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey={`${Namespaces.OrganizationAdmin}:oidc.hint.origin_url`}
                    values={{ url: BasicRoutes.LOGIN }}
                    components={{ b: <b /> }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey={`${Namespaces.OrganizationAdmin}:oidc.hint.redirect_url`}
                    values={{ url: BasicRoutes.SSO_CALLBACK }}
                    components={{ b: <b /> }}
                  />
                </li>
              </ul>
            </Typography>
            {oidcConfig && !teams?.some(team => team.ssoGroups.length) ? (
              <Alert variant="outlined" severity="info" sx={{ mt: 2 }}>
                {t('organizationAdmin:oidc.teamMappingInfo.nothingSetUp')}
                <br />
                {t('organizationAdmin:oidc.teamMappingInfo.currentBehaviour')}
                <br />
                {t('organizationAdmin:oidc.teamMappingInfo.listCTA')}
                <ol>
                  <li>{t('organizationAdmin:oidc.teamMappingInfo.listStep1')}</li>
                  <li>{t('organizationAdmin:oidc.teamMappingInfo.listStep2')}</li>
                  <li>{t('organizationAdmin:oidc.teamMappingInfo.listStep3')}</li>
                </ol>
                {t('organizationAdmin:oidc.teamMappingInfo.afterBehaviour')}
              </Alert>
            ) : null}
          </div>
        </div>
      )}
    </Formik>
  );
});
